import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';  // ודא שהקובץ הזה קיים במיקום הזה
import { AlertCircle, Save, Image, Calendar, FileText, Trash2, ChevronLeft, Edit, Lock } from 'lucide-react';
import { EditorToolbar } from '../components/EditorToolbar';

interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  image_url?: string;
  published_at: string;
  slug: string;
}

const AdminPage: React.FC = () => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        
        setSession(session);
        setLoading(false);
      } catch (err) {
        console.error('Error checking session:', err);
        setError('אירעה שגיאה בבדיקת ההרשאות');
        setLoading(false);
      }
    };

    checkSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      if (!data.session) throw new Error('No session returned');

      setSession(data.session);
    } catch (err) {
      console.error('Error signing in:', err);
      setError('שם משתמש או סיסמה שגויים');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-center justify-center mb-6">
              <Lock className="w-8 h-8 text-blue-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              כניסה למערכת ניהול
            </h2>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  דואר אלקטרוני
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border-2 border-gray-200 focus:border-blue-400 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  סיסמה
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border-2 border-gray-200 focus:border-blue-400 focus:outline-none"
                  required
                />
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className={`w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? 'מתחבר...' : 'התחבר'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex gap-8">
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="flex justify-between items-center mb-8">
                <button
                  onClick={() => supabase.auth.signOut()}
                  className="text-gray-600 hover:text-gray-800 transition-colors"
                >
                  התנתק
                </button>
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingPost ? 'עריכת מאמר' : 'מאמר חדש'}
                </h2>
              </div>
              <BlogPostForm 
                editingPost={editingPost} 
                onCancel={() => setEditingPost(null)}
              />
            </div>
          </div>
          <div className="w-96">
            <BlogPostsList onEdit={setEditingPost} />
          </div>
        </div>
      </div>
    </div>
  );
};

interface BlogPostFormProps {
  editingPost: BlogPost | null;
  onCancel: () => void;
}
const generateUniqueSlug = (title: string) => {
  const baseSlug = title
    .toLowerCase()
    .trim()
    .replace(/[^א-תa-z0-9\s]/g, '')
    .replace(/\s+/g, '-');

  const timestamp = Date.now().toString().slice(-6);
  return `${baseSlug}-${timestamp}`;
};


const BlogPostForm: React.FC<BlogPostFormProps> = ({ editingPost, onCancel }) => {
  const [title, setTitle] = useState(editingPost?.title || '');
  const [content, setContent] = useState(editingPost?.content || '');
  const [excerpt, setExcerpt] = useState(editingPost?.excerpt || '');
  const [imageUrl, setImageUrl] = useState(editingPost?.image_url || '');
  const [publishedAt, setPublishedAt] = useState(
    editingPost?.published_at 
      ? new Date(editingPost.published_at).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (editingPost) {
      setTitle(editingPost.title);
      setContent(editingPost.content);
      setExcerpt(editingPost.excerpt);
      setImageUrl(editingPost.image_url || '');
      setPublishedAt(new Date(editingPost.published_at).toISOString().split('T')[0]);
    }
  }, [editingPost]);

  const validateForm = () => {
    if (!title.trim()) {
      setError('כותרת היא שדה חובה');
      return false;
    }
    if (!content.trim()) {
      setError('תוכן המאמר הוא שדה חובה');
      return false;
    }
    if (!excerpt.trim()) {
      setError('תקציר הוא שדה חובה');
      return false;
    }
    if (!imageUrl.trim()) {
      setError('תמונת כותרת היא שדה חובה');
      return false;
    }
    if (!isValidUrl(imageUrl)) {
      setError('כתובת התמונה אינה תקינה');
      return false;
    }
    return true;
  };

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return url.startsWith('https://') && (
        url.includes('unsplash.com') || 
        url.includes('images.pexels.com') ||
        url.includes('cloudinary.com')
      );
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      const postData = {
        title,
        content,
        excerpt,
        author: 'צוות מתי כדאי',
        published_at: new Date(publishedAt).toISOString(),
        is_published: true,
        image_url: imageUrl,
      };

      if (editingPost) {
        // Update existing post
        const { data, error: updateError } = await supabase
          .from('blog_posts')
          .update(postData)
          .eq('id', editingPost.id)
          .select();

        if (updateError) throw updateError;
        if (!data) throw new Error('לא התקבל אישור מהשרת');
      } else {
        // Create new post
        const slug = generateUniqueSlug(title);

        const { data, error: insertError } = await supabase
          .from('blog_posts')
          .insert([{ ...postData, slug }])
          .select();

        if (insertError) throw insertError;
        if (!data) throw new Error('לא התקבל אישור מהשרת');
      }

      setSuccess(true);
      if (!editingPost) {
        setTitle('');
        setContent('');
        setExcerpt('');
        setImageUrl('');
        setPublishedAt(new Date().toISOString().split('T')[0]);
      }
      onCancel();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Error saving post:', err);
      setError(`אירעה שגיאה בשמירת המאמר: ${err instanceof Error ? err.message : 'שגיאה לא ידועה'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditorToolbarInsert = (text: string) => {
    setContent(content + text);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-gray-700 font-medium mb-2">
          <FileText className="inline-block w-5 h-5 ml-2" />
          כותרת
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            setError(null);
          }}
          required
          className="w-full px-4 py-2 text-right rounded-lg border-2 border-gray-200 focus:border-blue-400 focus:outline-none"
          dir="rtl"
        />
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-2">
          <FileText className="inline-block w-5 h-5 ml-2" />
          תוכן
        </label>
        <EditorToolbar onInsert={handleEditorToolbarInsert} />
        <textarea
          value={content}
          onChange={(e) => {
            setContent(e.target.value);
            setError(null);
          }}
          required
          rows={15}
          className="w-full px-4 py-2 text-right rounded-lg border-2 border-gray-200 focus:border-blue-400 focus:outline-none"
          dir="rtl"
        />
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-2">
          <FileText className="inline-block w-5 h-5 ml-2" />
          תקציר
        </label>
        <textarea
          value={excerpt}
          onChange={(e) => {
            setExcerpt(e.target.value);
            setError(null);
          }}
          required
          rows={3}
          className="w-full px-4 py-2 text-right rounded-lg border-2 border-gray-200 focus:border-blue-400 focus:outline-none"
          dir="rtl"
        />
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-2">
          <Image className="inline-block w-5 h-5 ml-2" />
          קישור לתמונת כותרת
        </label>
        <input
          type="url"
          value={imageUrl}
          onChange={(e) => {
            setImageUrl(e.target.value);
            setError(null);
          }}
          placeholder="https://images.unsplash.com/photo-..."
          required
          className="w-full px-4 py-2 text-left rounded-lg border-2 border-gray-200 focus:border-blue-400 focus:outline-none"
        />
        <p className="text-sm text-gray-500 mt-1">
          * ניתן להשתמש בתמונות מ-Unsplash, Pexels או Cloudinary בלבד
        </p>
        {imageUrl && (
          <div className="mt-2">
            <img 
              src={imageUrl} 
              alt="תצוגה מקדימה" 
              className="max-w-xs rounded-lg shadow-sm"
              onError={() => setError('לא ניתן לטעון את התמונה. אנא בדוק את הקישור.')}
            />
          </div>
        )}
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-2">
          <Calendar className="inline-block w-5 h-5 ml-2" />
          תאריך פרסום
        </label>
        <input
          type="date"
          value={publishedAt}
          onChange={(e) => setPublishedAt(e.target.value)}
          required
          className="w-full px-4 py-2 rounded-lg border-2 border-gray-200 focus:border-blue-400 focus:outline-none"
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 p-4 rounded-lg">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 text-green-600 bg-green-50 p-4 rounded-lg">
          <Save className="w-5 h-5" />
          <span>המאמר נשמר בהצלחה!</span>
        </div>
      )}

      <div className="flex gap-4">
        {editingPost && (
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
          >
            ביטול
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitting}
          className={`flex-1 bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 ${
            isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <Save className="w-5 h-5" />
          {isSubmitting ? 'שומר...' : editingPost ? 'שמור שינויים' : 'פרסם מאמר'}
        </button>
      </div>
    </form>
  );
};

interface BlogPostsListProps {
  onEdit: (post: BlogPost) => void;
}

const BlogPostsList: React.FC<BlogPostsListProps> = ({ onEdit }) => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('published_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('אירעה שגיאה בטעינת המאמרים');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('האם אתה בטוח שברצונך למחוק מאמר זה?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setPosts(posts.filter(post => post.id !== id));
    } catch (err) {
      console.error('Error deleting post:', err);
      alert('אירעה שגיאה במחיקת המאמר');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h3 className="text-xl font-bold text-gray-800 mb-6">מאמרים קיימים</h3>
      <div className="space-y-4">
        {posts.map(post => (
          <div key={post.id} className="border-b pb-4 last:border-0">
            <h4 className="font-medium text-gray-800 mb-2">{post.title}</h4>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Calendar className="w-4 h-4" />
              <span>{new Date(post.published_at).toLocaleDateString('he-IL')}</span>
            </div>
            {post.image_url && (
              <img 
                src={post.image_url} 
                alt={post.title}
                className="w-full h-24 object-cover rounded-lg mt-2"
              />
            )}
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={() => handleDelete(post.id)}
                className="text-red-600 hover:text-red-800 transition-colors"
                title="מחק מאמר"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => onEdit(post)}
                className="text-blue-600 hover:text-blue-800 transition-colors"
                title="ערוך מאמר"
              >
                <Edit className="w-4 h-4" />
              </button>
              <a
                href={`/blog/${post.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-800 transition-colors"
                title="צפה במאמר"
              >
                <ChevronLeft className="w-4 h-4" />
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminPage;
